import React, { useMemo, useState } from 'react';
import type { AssignmentCoverImage } from '../../types/assignmentPointsNote';
import CachedAssignmentCoverImage from '../common/CachedAssignmentCoverImage';
import AssignmentCoverImageModal from './AssignmentCoverImageModal';

export type AssignmentCoverPickFieldProps = {
  coverImageId?: string;
  coverImages: AssignmentCoverImage[];
  onCoverImageIdChange: (id: string | undefined) => void;
  onCoverImagesChange: (images: AssignmentCoverImage[]) => void;
  /** 이름·설명 필드 */
  children: React.ReactNode;
};

/** 과제 생성/수정: 왼쪽 커버 슬롯 + 오른쪽 이름·설명 */
const AssignmentCoverPickField: React.FC<AssignmentCoverPickFieldProps> = ({
  coverImageId,
  coverImages,
  onCoverImageIdChange,
  onCoverImagesChange,
  children,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const selected = useMemo(
    () => coverImages.find((c) => c.id === coverImageId) ?? null,
    [coverImages, coverImageId]
  );

  return (
    <>
      <section
        className="cp-apn-create-section cp-apn-create-cover-section"
        data-tutorial-apn-create="basics"
      >
        <div className="cp-apn-create-cover-layout">
          <button
            type="button"
            className={`cp-apn-create-cover-slot${selected ? ' has-image' : ''}`}
            onClick={() => setModalOpen(true)}
            aria-label={selected ? '과제 이미지 변경' : '과제 이미지 추가'}
          >
            {selected ? (
              <CachedAssignmentCoverImage
                image={selected}
                className="cp-apn-create-cover-slot-img"
                fallback={
                  <span className="cp-apn-create-cover-slot-empty">
                    <span className="cp-apn-create-cover-slot-plus" aria-hidden>
                      +
                    </span>
                  </span>
                }
              />
            ) : (
              <span className="cp-apn-create-cover-slot-empty">
                <span className="cp-apn-create-cover-slot-plus" aria-hidden>
                  +
                </span>
              </span>
            )}
          </button>
          <div className="cp-apn-create-cover-fields">{children}</div>
        </div>
        {selected ? (
          <div className="cp-apn-create-cover-actions">
            <button
              type="button"
              className="cp-apn-create-cover-clear"
              onClick={() => onCoverImageIdChange(undefined)}
            >
              이미지 선택 해제
            </button>
          </div>
        ) : null}
      </section>

      <AssignmentCoverImageModal
        open={modalOpen}
        coverImages={coverImages}
        selectedCoverImageId={coverImageId}
        onClose={() => setModalOpen(false)}
        onSelectExisting={(id) => onCoverImageIdChange(id)}
        onUploaded={(image) => {
          onCoverImagesChange([image, ...coverImages]);
          onCoverImageIdChange(image.id);
        }}
        onDeleteLibraryImage={(id) => {
          onCoverImagesChange(coverImages.filter((c) => c.id !== id));
          if (coverImageId === id) onCoverImageIdChange(undefined);
        }}
      />
    </>
  );
};

export default AssignmentCoverPickField;
